import React, { useRef, useLayoutEffect } from 'react';

function execCmd(cmd, value = null) {
  document.execCommand(cmd, false, value);
  document.getElementById('faq-editor-active')?.focus();
}

export default function RichTextEditor({ value = '', onChange, placeholder, minHeight = '100px' }) {
  const editorRef = useRef(null);
  const lastSyncedRef = useRef(undefined); // what we last wrote to the editor from props

  // Sync value from parent when it changes (e.g. switching FAQ item)
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = (value || '').trim();
    if (lastSyncedRef.current === value) return;
    lastSyncedRef.current = value;
    el.innerHTML = html;
    el.classList.toggle('is-empty', !el.textContent?.trim());
  }, [value]);

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML || '';
    lastSyncedRef.current = html;
    onChange(html);
    el.classList.toggle('is-empty', !el.textContent?.trim());
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="rich-text-editor-wrapper" style={{ marginBottom: '4px' }}>
      <div className="rte-toolbar">
        <button type="button" title="Bold" onClick={() => execCmd('bold')} aria-label="Bold">
          <b>B</b>
        </button>
        <button type="button" title="Italic" onClick={() => execCmd('italic')} aria-label="Italic">
          <i>I</i>
        </button>
        <button type="button" title="Insert link" onClick={() => {
          const url = window.prompt('Link URL:');
          if (url) execCmd('createLink', url);
        }} aria-label="Insert link">
          🔗
        </button>
        <button type="button" title="Bullet list" onClick={() => execCmd('insertUnorderedList')} aria-label="Bullet list">
          •
        </button>
        <button type="button" title="Numbered list" onClick={() => execCmd('insertOrderedList')} aria-label="Numbered list">
          1.
        </button>
      </div>
      <div
        id="faq-editor-active"
        ref={editorRef}
        className="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        style={{ minHeight }}
      />
      <style>{`
        .rich-text-editor-wrapper {
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          overflow: hidden;
          background: rgba(0, 0, 0, 0.2);
        }
        .rte-toolbar {
          display: flex;
          gap: 2px;
          padding: 6px 8px;
          background: rgba(0, 0, 0, 0.25);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }
        .rte-toolbar button {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 0.95rem;
        }
        .rte-toolbar button:hover {
          color: var(--accent-gold);
          background: rgba(255, 255, 255, 0.08);
        }
        .rte-editor {
          padding: 10px 12px;
          font-family: var(--font-body);
          font-size: 1rem;
          color: var(--text-primary);
          outline: none;
          line-height: 1.6;
        }
        .rte-editor:empty::before,
        .rte-editor.is-empty::before {
          content: attr(data-placeholder);
          color: rgba(255, 255, 255, 0.5);
        }
        .rte-editor a {
          color: var(--accent-gold);
          text-decoration: underline;
        }
        .rte-editor ul, .rte-editor ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
      `}</style>
    </div>
  );
}
